import React from "react";
import { ApiRegistration } from "src/api/types";
import Alert from "src/components/Alert";
import Button from "src/components/Button";
import Card from "src/components/Card";
import ChatButton from "src/components/forms/TaskForm/elements/ChatButton";
import Tabs from "src/components/layout/Tabs";
import useTask from "src/hooks/api/tasks/useTask";
import RegistrationButtons from "src/components/tasks/panes/registration/RegistrationButtons";
import RegistrationDetails from "src/components/tasks/panes/registration/RegistrationDetails";
import RegistrationReviews from "src/components/tasks/panes/registration/RegistrationReviews";
import RegistrationTaskDetails from "src/components/tasks/panes/registration/RegistrationTaskDetails";
import RegistrationContactDetails from "src/components/tasks/panes/registration/student/RegistrationContactDetails";
import { useTranslation } from "react-i18next";
import moment from "moment";

interface RegistrationMatchProps {
	registration: ApiRegistration;
}

const RegistrationMatch = ({ registration }: RegistrationMatchProps) => {
	const { t } = useTranslation("registrations", {
		keyPrefix: "registration-match",
	});
	const { task } = useTask();
	const tabs = [t("tabs.information"), t("tabs.registration")];

	if (task?.contact) {
		tabs.push(t("tabs.contact"));
	}

	if (registration?.is_accepted) {
		tabs.push(t("tabs.reviews"));
	}

	return (
		<Card className="flex-1" bg="white">
			<div className="flex flex-col gap-6">
				{registration?.is_denied && (
					<Alert type="danger">{t("denied")}</Alert>
				)}
				<div className="flex items-center gap-4">
					<Button to={`/tasks`} type="dark" iconOnly>
						<i className="fas fa-arrow-left"></i>
					</Button>
					<h2>{task.title}</h2>
				</div>
				<hr className="border-dark opacity-10" />
				<Tabs
					{...{ tabs }}
					options={{
						noBackground: true,
						scrollToActive: true,
						small: true,
					}}
					header={<hr className="border-dark opacity-10 mt-6" />}
					actions={{
						right: (
							<>
								{task.variant === "HOUR_RATE" &&
									registration.status === "matched" && (
										<Button
											to={`/hours/${task.hid}/show`}
											type="dark"
											disabled={
												!moment(task.end_date).isBefore(
													moment()
												)
											}
										>
											<i className="fas mr-2 fa-alarm-clock"></i>
											{t("tabs.hours")}
										</Button>
									)}
								<ChatButton
									className="gtm-student-dashboard-opdracht--button-naar-chat"
									to={`/tasks/${task.hid}/show/${registration?.hid}/chat`}
									registrationId={registration?.id}
								/>
							</>
						),
					}}
				>
					{(tab) => {
						if (tab === t("tabs.registration")) {
							return (
								<RegistrationDetails {...{ registration }} />
							);
						}
						if (tab === t("tabs.reviews")) {
							return (
								<RegistrationReviews
									{...{
										registration,
									}}
								/>
							);
						}

						if (tab === t("tabs.contact")) {
							return (
								<RegistrationContactDetails
									contact={task.contact}
									subject={t("contact.subject", {
										title: task.title,
									})}
								/>
							);
						}

						return (
							<RegistrationTaskDetails {...{ registration }} />
						);
					}}
				</Tabs>
				<hr className="border-dark opacity-10" />
				<RegistrationButtons {...{ registration }} />
			</div>
		</Card>
	);
};

export default RegistrationMatch;
